// frontend/src/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { checkVencimentos, showVencimentoAlert } from './services/notificationService';
import { FaBell, FaExclamationTriangle, FaCalendar, FaMoneyBillWave, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';
import WidgetMonitoramento from './modules/monitoramento/components/WidgetMonitoramento';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

function Dashboard({ usuario }) {
  const [resumo, setResumo] = useState({
    totalReceitas: 0,
    totalDespesas: 0,
    saldo: 0,
    lancamentos: [],
    resumoPorCategoria: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alertaVencimento, setAlertaVencimento] = useState(null);
  const [alertasDissidio, setAlertasDissidio] = useState([]);
  const [dissidioConfig, setDissidioConfig] = useState(null);

  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isTablet = useMediaQuery({ maxWidth: 992 });
  const empresaId = usuario?.empresa_id || 1;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDados();
    fetchDissidioAlertas();
    fetchDissidioConfig();
  }, [empresaId]);

  // ============================================
  // BUSCAR DADOS DO DASHBOARD
  // ============================================
  const fetchDados = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/lancamentos/empresa/${empresaId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro:', errorText);
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('📦 Dados do dashboard:', data.length, 'lançamentos');
      
      let totalReceitas = 0;
      let totalDespesas = 0;
      const resumoPorCategoria = {};

      data.forEach(item => {
        const valor = Number(item.valor) || 0;
        
        if (item.tipo === 'RECEITA') {
          totalReceitas += valor;
        } else if (item.tipo === 'DESPESA') {
          totalDespesas += valor;
        }

        const categoria = item.categoria || 'Sem categoria';
        if (!resumoPorCategoria[categoria]) {
          resumoPorCategoria[categoria] = { receitas: 0, despesas: 0 };
        }
        if (item.tipo === 'RECEITA') {
          resumoPorCategoria[categoria].receitas += valor;
        } else {
          resumoPorCategoria[categoria].despesas += valor;
        }
      });

      setResumo({
        totalReceitas,
        totalDespesas,
        saldo: totalReceitas - totalDespesas,
        lancamentos: data,
        resumoPorCategoria
      });

      const { vencidos, vencemHoje } = checkVencimentos(data);
      const alerta = showVencimentoAlert(vencidos, vencemHoje);
      setAlertaVencimento(alerta);
      
    } catch (err) {
      console.error('❌ Erro detalhado:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // BUSCAR ALERTAS DE DISSÍDIO
  // ============================================
  const fetchDissidioAlertas = async () => {
    try {
      const response = await fetch(`/api/dissidio/alertas/${empresaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const naoLidos = data.filter(a => !a.lido);
        setAlertasDissidio(naoLidos);
      }
    } catch (err) {
      console.error('Erro ao buscar alertas de dissídio:', err);
    }
  };

  // ============================================
  // BUSCAR CONFIGURAÇÃO DE DISSÍDIO
  // ============================================
  const fetchDissidioConfig = async () => {
    try {
      const response = await fetch(`/api/dissidio/config/${empresaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.mes_data_base) {
          setDissidioConfig(data);
        }
      }
    } catch (err) {
      console.error('Erro ao buscar configuração de dissídio:', err);
    }
  };

  // ============================================
  // MARCAR ALERTA COMO LIDO
  // ============================================
  const marcarAlertaComoLido = async (alertaId) => {
    try {
      const response = await fetch(`/api/dissidio/alerta/${alertaId}/ler`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAlertasDissidio(alertasDissidio.filter(a => a.id !== alertaId));
      }
    } catch (err) {
      console.error('Erro ao marcar alerta como lido:', err);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Opções do gráfico para mobile
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: isMobile ? 'bottom' : 'top',
        labels: { font: { size: isMobile ? 10 : 12 } }
      },
      title: { 
        display: true, 
        text: '📊 Resumo Financeiro',
        font: { size: isMobile ? 12 : 16 }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          font: { size: isMobile ? 8 : 12 },
          callback: function(value) {
            return isMobile ? `R$ ${(value/1000).toFixed(0)}k` : 'R$ ' + value.toFixed(2);
          }
        }
      },
      x: {
        ticks: {
          font: { size: isMobile ? 10 : 12 }
        }
      }
    }
  };

  // Dados para o gráfico de barras
  const barData = {
    labels: ['Receitas', 'Despesas', 'Saldo'],
    datasets: [
      {
        label: 'Valores (R$)',
        data: [resumo.totalReceitas, resumo.totalDespesas, resumo.saldo],
        backgroundColor: ['rgba(40, 167, 69, 0.7)', 'rgba(220, 53, 69, 0.7)', 'rgba(0, 123, 255, 0.7)'],
        borderColor: ['#28a745', '#dc3545', '#007bff'],
        borderWidth: 2,
      },
    ],
  };

  // Opções do gráfico de pizza para mobile
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: isMobile ? 'bottom' : 'right',
        labels: { 
          font: { size: isMobile ? 10 : 12 },
          boxWidth: isMobile ? 12 : 20
        }
      },
      title: { 
        display: true, 
        text: '🍩 Saldo por Categoria',
        font: { size: isMobile ? 12 : 16 }
      }
    }
  };

  // Dados para o gráfico de pizza
  const categorias = Object.keys(resumo.resumoPorCategoria);
  const cores = ['#28a745', '#dc3545', '#ffc107', '#17a2b8', '#6f42c1', '#fd7e14', '#20c997', '#e83e8c'];
  
  const doughnutData = {
    labels: categorias,
    datasets: [
      {
        data: categorias.map(cat => {
          const totals = resumo.resumoPorCategoria[cat];
          return totals.receitas - totals.despesas;
        }),
        backgroundColor: cores.slice(0, categorias.length),
        borderColor: cores.slice(0, categorias.length).map(c => c),
        borderWidth: 1,
      },
    ],
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>🔄 Carregando dados...</div>;
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div style={{ fontSize: '48px', marginBottom: '10px' }}>❌</div>
        <p style={{ color: 'red' }}>Erro: {error}</p>
        <button 
          onClick={fetchDados}
          style={{ 
            marginTop: '10px', 
            padding: '10px 20px', 
            backgroundColor: '#007bff', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer' 
          }}
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '10px' : '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        marginBottom: '20px',
        gap: '10px'
      }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '28px' }}>📊 Dashboard Financeiro</h2>
        <button 
          onClick={() => window.location.href = '/dissidio'}
          style={{
            padding: isMobile ? '6px 12px' : '8px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: isMobile ? '12px' : '14px',
            width: isMobile ? '100%' : 'auto',
            justifyContent: 'center'
          }}
        >
          <FaCalendar /> Configurar Dissídio
        </button>
      </div>

      {/* Widget de Monitoramento RF */}
      <div style={{ marginBottom: '20px' }}>
        <WidgetMonitoramento 
          empresaId={empresaId} 
          token={token} 
        />
      </div>

      {/* Alertas de Dissídio */}
      {alertasDissidio.length > 0 && (
        <div style={{ 
          backgroundColor: '#cce5ff', 
          border: '1px solid #b8daff', 
          borderRadius: '8px', 
          padding: isMobile ? '12px' : '15px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <FaBell style={{ color: '#004085' }} />
            <strong style={{ color: '#004085', fontSize: isMobile ? '13px' : '16px' }}>⚠️ Alertas de Dissídio</strong>
          </div>
          {alertasDissidio.map(alerta => (
            <div key={alerta.id} style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between', 
              alignItems: isMobile ? 'stretch' : 'center',
              backgroundColor: 'white',
              padding: isMobile ? '10px' : '10px 15px',
              borderRadius: '4px',
              marginBottom: '8px',
              gap: isMobile ? '8px' : '0'
            }}>
              <div>
                <p style={{ margin: 0, fontWeight: 'bold', color: '#004085', fontSize: isMobile ? '13px' : '14px' }}>
                  <FaExclamationTriangle style={{ marginRight: '8px', color: '#ffc107' }} />
                  {alerta.titulo}
                </p>
                <p style={{ margin: '5px 0 0 0', fontSize: isMobile ? '12px' : '14px', color: '#6c757d' }}>
                  {alerta.descricao}
                </p>
                <small style={{ color: '#004085' }}>
                  Faltam {alerta.dias_restantes} dias
                </small>
              </div>
              <button
                onClick={() => marcarAlertaComoLido(alerta.id)}
                style={{
                  padding: isMobile ? '5px 12px' : '5px 15px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '12px' : '14px',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Marcar como Lido
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Resumo do Dissídio */}
      {dissidioConfig && (
        <div style={{ 
          backgroundColor: '#e8f5e9', 
          border: '1px solid #c8e6c9', 
          borderRadius: '8px', 
          padding: isMobile ? '12px' : '15px',
          marginBottom: '20px'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '10px' : '0'
          }}>
            <div>
              <p style={{ margin: 0, fontWeight: 'bold', color: '#2e7d32', fontSize: isMobile ? '13px' : '16px' }}>
                ⚖️ Data-Base da Categoria
              </p>
              <p style={{ margin: '5px 0 0 0', color: '#2e7d32', fontSize: isMobile ? '12px' : '14px' }}>
                {dissidioConfig.dia_data_base} de {['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'][dissidioConfig.mes_data_base - 1]}
                {' • '}Categoria: {dissidioConfig.categoria || 'Não definida'}
              </p>
            </div>
            <button
              onClick={() => window.location.href = '/dissidio'}
              style={{
                padding: isMobile ? '5px 12px' : '5px 15px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: isMobile ? '12px' : '14px',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              Editar
            </button>
          </div>
        </div>
      )}

      {/* Alertas de Vencimento */}
      {alertaVencimento && (
        <div style={{ 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffc107', 
          color: '#856404',
          padding: isMobile ? '12px' : '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          fontSize: isMobile ? '13px' : '16px'
        }}>
          <strong>⚠️ Atenção!</strong> {alertaVencimento}
        </div>
      )}
      
      {/* Cards Financeiros - Responsivos */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 1fr' : 'repeat(3, 1fr)', 
        gap: isMobile ? '10px' : '20px', 
        marginBottom: '30px' 
      }}>
        <div style={{ backgroundColor: '#d4edda', padding: isMobile ? '15px' : '20px', borderRadius: '8px', textAlign: 'center' }}>
          <h3 style={{ margin: 0, color: '#155724', fontSize: isMobile ? '14px' : '18px' }}>💰 Receitas</h3>
          <p style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#155724' }}>
            {formatCurrency(resumo.totalReceitas)}
          </p>
        </div>
        <div style={{ backgroundColor: '#f8d7da', padding: isMobile ? '15px' : '20px', borderRadius: '8px', textAlign: 'center' }}>
          <h3 style={{ margin: 0, color: '#721c24', fontSize: isMobile ? '14px' : '18px' }}>💸 Despesas</h3>
          <p style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#721c24' }}>
            {formatCurrency(resumo.totalDespesas)}
          </p>
        </div>
        <div style={{ 
          backgroundColor: '#cce5ff', 
          padding: isMobile ? '15px' : '20px', 
          borderRadius: '8px', 
          textAlign: 'center',
          gridColumn: isMobile ? '1' : isTablet ? '1 / -1' : 'auto'
        }}>
          <h3 style={{ margin: 0, color: '#004085', fontSize: isMobile ? '14px' : '18px' }}>📈 Saldo</h3>
          <p style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#004085' }}>
            {formatCurrency(resumo.saldo)}
          </p>
        </div>
      </div>

      {/* Gráficos - Responsivos */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', 
        gap: isMobile ? '15px' : '20px', 
        marginBottom: '30px' 
      }}>
        <div style={{ 
          backgroundColor: 'white', 
          padding: isMobile ? '10px' : '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          height: isMobile ? '250px' : '350px'
        }}>
          <Bar data={barData} options={barOptions} />
        </div>
        <div style={{ 
          backgroundColor: 'white', 
          padding: isMobile ? '10px' : '20px', 
          borderRadius: '8px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          height: isMobile ? '250px' : '350px'
        }}>
          {categorias.length > 0 ? (
            <Doughnut data={doughnutData} options={doughnutOptions} />
          ) : (
            <p style={{ textAlign: 'center', padding: '40px 0' }}>📭 Sem dados por categoria</p>
          )}
        </div>
      </div>

      {/* Últimos Lançamentos - Responsivo */}
      <div style={{ backgroundColor: 'white', padding: isMobile ? '10px' : '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <h3 style={{ fontSize: isMobile ? '16px' : '20px' }}>📋 Últimos Lançamentos</h3>
        {resumo.lancamentos.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '20px' }}>📭 Nenhum lançamento encontrado.</p>
        ) : isMobile ? (
          // Mobile - Cards
          <div>
            {resumo.lancamentos.slice(0, 5).map((item, index) => (
              <div key={index} style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '10px',
                borderLeft: `4px solid ${item.tipo === 'RECEITA' ? '#28a745' : '#dc3545'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ fontSize: '14px' }}>{item.descricao}</strong>
                  <span style={{ fontWeight: 'bold', color: item.tipo === 'RECEITA' ? '#28a745' : '#dc3545' }}>
                    {formatCurrency(item.valor)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', fontSize: '12px', color: '#6c757d' }}>
                  <span>{new Date(item.data_lancamento).toLocaleDateString('pt-BR')}</span>
                  <span>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      backgroundColor: item.tipo === 'RECEITA' ? '#d4edda' : '#f8d7da',
                      color: item.tipo === 'RECEITA' ? '#155724' : '#721c24',
                      fontSize: '10px'
                    }}>
                      {item.tipo}
                    </span>
                    {' '}
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      backgroundColor: item.status === 'PAGO' ? '#d4edda' : '#fff3cd',
                      color: item.status === 'PAGO' ? '#155724' : '#856404',
                      fontSize: '10px'
                    }}>
                      {item.status}
                    </span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Desktop - Tabela
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Data</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Descrição</th>
                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Valor</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Tipo</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {resumo.lancamentos.slice(0, 5).map((item, index) => (
                <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '10px' }}>{new Date(item.data_lancamento).toLocaleDateString('pt-BR')}</td>
                  <td style={{ padding: '10px' }}>{item.descricao}</td>
                  <td style={{ padding: '10px', textAlign: 'right', color: item.tipo === 'RECEITA' ? '#28a745' : '#dc3545' }}>
                    {formatCurrency(item.valor)}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      backgroundColor: item.tipo === 'RECEITA' ? '#d4edda' : '#f8d7da',
                      color: item.tipo === 'RECEITA' ? '#155724' : '#721c24'
                    }}>
                      {item.tipo}
                    </span>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      backgroundColor: item.status === 'PAGO' ? '#d4edda' : '#fff3cd',
                      color: item.status === 'PAGO' ? '#155724' : '#856404'
                    }}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Dashboard;